// $1.50 Wing Wednesday — Burbank & NoHo only.
//
// Temporarily replaces Fish Taco Wednesday (which is off via
// `fishTacoWednesdayEnabled` in ./index.js). The promo category is built
// client-side and prepended to the menu in src/Context/applyDiscount.js, so
// nothing has to change in the POS menu. Flip `wingWednesdayEnabled` to false
// to retire it everywhere — banners, modal and menu category all key off it.

export const wingWednesdayEnabled = true

export const wingWednesdayShopIds = [
  'pablitostacosburbank',
  'pablitostacosnoho',
]

export const WING_PRICE = 1.5
export const WING_MINIMUM_PER_FLAVOR = 4

// Returns true when today (in America/Los_Angeles) is Wednesday, regardless
// of the user's device timezone.
export function isLAWednesday() {
  try {
    const day = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      timeZone: 'America/Los_Angeles',
    }).format(new Date())
    return day === 'Wed'
  } catch (e) {
    return new Date().getDay() === 3
  }
}

export function isWingWednesday() {
  return wingWednesdayEnabled && isLAWednesday()
}

export function isWingWednesdayShop(shopId) {
  return wingWednesdayShopIds.indexOf(shopId) !== -1
}

export function isWingWednesdayLive(shopId) {
  return isWingWednesdayShop(shopId) && isWingWednesday()
}

// `keywords` match the flavor against the shop's own wings products so the
// promo can borrow their photos — Burbank and NoHo spell some flavors
// differently ("Sriracha"/"Siracha") and host photos in separate buckets.
const FLAVORS = [
  {id: 'ww-buffalo', name: 'Buffalo Wings', keywords: ['buffalo']},
  {id: 'ww-sriracha', name: 'Sriracha Wings', keywords: ['sriracha', 'siracha']},
  {id: 'ww-garlic-parmesan', name: 'Garlic Parmesan Wings', keywords: ['garlic parmesan', 'parmesan']},
  {id: 'ww-mango-habanero', name: 'Mango Habanero Wings', keywords: ['mango']},
  {id: 'ww-kickin-bourbon', name: 'Kickin Bourbon Wings', keywords: ['bourbon']},
  {id: 'ww-honey-garlic', name: 'Honey Garlic Wings', keywords: ['honey garlic']},
  {id: 'ww-original-yellow', name: 'Original Pablitos Yellow Wings', keywords: ['yellow']},
]

function findFlavorPhoto(menu, keywords) {
  const subMenus = (menu && menu.subMenus) || []
  for (let subMenu of subMenus) {
    for (let product of (subMenu.products || [])) {
      const name = ((product.name && product.name.en) || '').toLowerCase()
      if (name.indexOf('wing') === -1) { continue }
      if (!product.photo) { continue }
      if (keywords.some((keyword) => name.indexOf(keyword) !== -1)) {
        return product.photo
      }
    }
  }
  return null
}

// Builds the promo category from the shop's live menu. Returns null when the
// shop has no wings on the menu at all, so we never show an empty promo.
export function buildWingWednesdayCategory(menu) {
  let foundAnyWings = false
  const modifiers = FLAVORS.map(({id, name, keywords}) => {
    const photo = findFlavorPhoto(menu, keywords)
    if (photo) { foundAnyWings = true }
    const modifier = {id, name, price: WING_PRICE}
    if (photo) { modifier.photo = photo }
    return modifier
  })

  const heroPhoto = findFlavorPhoto(menu, ['buffalo']) || findFlavorPhoto(menu, ['wing'])
  if (!heroPhoto && !foundAnyWings) { return null }

  const product = {
    id: 'wing-wednesday-build-your-own',
    name: {en: 'Wings'},
    description: {en: `Mix & match any flavors at $${WING_PRICE.toFixed(2)} per wing. Minimum ${WING_MINIMUM_PER_FLAVOR} wings of each flavor you pick. Wednesdays only.`},
    price: 0,
    tags: 'wing-wednesday-item',
    modgroups: [{
      id: 'wing-wednesday-flavors',
      name: `Choose Your Wings — $${WING_PRICE.toFixed(2)} each`,
      type: 'buffet',
      required: true,
      minEach: WING_MINIMUM_PER_FLAVOR,
      minEachLabel: 'flavor',
      modifiers,
    }],
  }
  if (heroPhoto) { product.photo = heroPhoto }

  return {
    id: 'wing-wednesday',
    // real categories all carry shopId — AutoMenu drops categories without one
    // when it is handed a shopId prop
    shopId: menu && menu.shopId,
    name: {en: `🔥 WING WEDNESDAY — $${WING_PRICE.toFixed(2)} Wings`},
    description: {en: `$${WING_PRICE.toFixed(2)} per wing, any flavor. Minimum ${WING_MINIMUM_PER_FLAVOR} per flavor. Today only.`},
    tag: 'wing-wednesday-promo',
    products: [product],
  }
}
